import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import client from "./services/interceptor";

import MainLayout from './layouts/MainLayout';
import Keywords from './views/Keywords';
import Research from './views/Research';
import TracerDecision from './views/TracerDecision';
import TracerSection from './views/TracerSection';
import Documents from './views/Documents';
import Summary from './components/explore/Summary';
import StoreProvider from './store/StoreProvider';
import ChatbotView from './views/ChatbotView';
import { ClerkProvider, RedirectToSignIn, SignedIn, SignedOut, useOrganization, useAuth } from "@clerk/clerk-react";
import NotFoundPage from './views/NotFoundPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UploadDocumentsPage from './views/UploadDocuments/UploadDocumentsPage';
import DHFView from './components/Documents/DHFView';
import EStarView from './components/Documents/EStarView';
import Typography from '@mui/material/Typography';
import DocumentDetailPage from './views/DocumentDetailPage'; 
import DocumentWizard from './components/Documents/DocumentWizard'; 
import Project from './views/Project';
import Predicates from './views/Predicates';
import SubscriptionPage from './components/billing/SubscriptionPage';

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable");
}

// This component will contain the logic that uses Clerk hooks
function AppRoutes() {
  const [activeView, setActiveView] = useState('requirements');
  const dashedLines = false;
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization();

  const { has } = useAuth(); // Use the useAuth hook to get the 'has' function

  // Determine if the organization has full access
  const hasFullAccess = isOrganizationLoaded && (organization?.publicMetadata?.full_access === "true" || has({ feature: 'full_access' }));

  if (!isOrganizationLoaded) {
    return null; // Or a loading spinner
  }

  const routeConfigs = [
    { path: "/Keywords", Component: Keywords, requiresFullAccess: false }, // Does not require full access
    { path: "/Research", Component: Research, requiresFullAccess: false }, // Does not require full access
    { path: "/Project", Component: Project, requiresFullAccess: true },    // Requires full access
    { path: "/Summary", Component: Summary, requiresFullAccess: true },    // Requires full access
    { path: "/DecisionTree", Component: TracerDecision, requiresFullAccess: true }, // Requires full access
    { path: "/Predicates", Component: Predicates, requiresFullAccess: true }, // Requires full access
    {
      path: "/Tracer-Requirements",
      Component: TracerSection,
      requiresFullAccess: true, // Requires full access
      props: { activeView, dashedLines },
    },
    {
      path: "/Documents",
      Component: Documents,
      requiresFullAccess: true, // Requires full access
      children: [
        { index: true, element: <Navigate to="upload" replace /> },
        { path: "upload", element: <UploadDocumentsPage /> },
        { path: "dhf", element: <DHFView /> },
        { path: "qms", element: <Typography sx={{ p: 3 }}>QMS View Coming Soon</Typography> },
        { path: "estar", element: <EStarView /> },
        { path: "Detail/:uuid", element: <DocumentDetailPage /> },
        { path: "Wizard/:uuid", element: <DocumentWizard /> },
      ],
    },
    { path: "/Chatbot", Component: ChatbotView, requiresFullAccess: true }, // Requires full access
  ];

  return (
    <ApolloProvider client={client}>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <Routes>
          <Route path="/" element={<Navigate to="/Keywords" replace />} />
          <Route element={<MainLayout activeView={activeView} setActiveView={setActiveView}><Outlet /></MainLayout>}>
            {routeConfigs.map(({ path, Component, requiresFullAccess, props = {}, children }) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute
                    hasFullAccess={hasFullAccess}
                    requiresFullAccess={requiresFullAccess}
                    element={<Component {...props} />}
                  />
                }
              >
                {children && children.map(childRoute => (
                  <Route
                    key={childRoute.path || (childRoute.index ? 'index' : 'unknown')}
                    index={childRoute.index}
                    path={childRoute.path}
                    element={childRoute.element}
                  />
                ))}
              </Route>
            ))}
            <Route path="/Subscribe" element={<SubscriptionPage />} />
          </Route>
        </Routes>
      </SignedIn>
    </ApolloProvider>
  );
}

function ClerkProviderWithRoutes() {
  const navigate = useNavigate();

  return (
    <ClerkProvider navigate={navigate} publishableKey={PUBLISHABLE_KEY}>
      <AppRoutes />
    </ClerkProvider>
  );
}

function App() {
  return (
    <StoreProvider>
      <Router>
        <ClerkProviderWithRoutes />
      </Router>
    </StoreProvider>
  );
}

export default App;
